import { hash01, placeKey } from '@/core/rng';
import type { Region } from './region';

/**
 * The island's remarkable places (Design III §12–§15).
 *
 * §15: *a good exploration world constantly whispers: go see that.* This one
 * did not whisper at all — `tools/fauna.ts` measured 71% of straight flights
 * over land meeting nothing whatever, a median 6.55 km between one thing worth
 * looking at and the next, and 43.7 km at worst.
 *
 * The fix is not to generate content. **The remarkable places are already
 * standing there.** The world is a pure function of position and seed (law 1),
 * so the waterfalls are already in `region.flow` crossing a step in
 * `region.elev`, the passes are already saddles in the same field, the crater
 * lakes are already the sinks the D8 routing had to fill, and the isolated
 * groves are already islands in `region.biome`. Nobody had ever asked the
 * region where they were.
 *
 * So a landmark is DERIVED, never authored and never stored:
 *
 * - **Law 1.** It is a pure function of the region, which is a pure function
 *   of seed. No camera, no clock, no visit order. Two machines find the same
 *   places in the same order.
 * - **Law 2.** Nothing goes in the snapshot. `CURRENT WORLD = BASELINE +
 *   SPARSE HISTORY` and a landmark is baseline — it costs one scan at load and
 *   nothing thereafter, exactly as an ordinary tree costs nothing.
 * - **Law 9.** Finding one writes nothing. The list is the same before and
 *   after a god looks at it, because there is nowhere for looking to be
 *   recorded. Identity is `placeKey` — *position is the name*, so the arch you
 *   found last session is the same arch, with no id ever minted.
 * - **Law 4** is what happens NEXT and deliberately not here: history may make
 *   a remarkable place sacred (`world/things.ts`), and that promotion is
 *   stored because it is divergence. Being remarkable is not.
 *
 * §14's warning — *if everything is extraordinary, nothing is* — is the whole
 * tuning problem, and it is why `tools/landmarks.ts` exists and why the cuts
 * below are absolute scores rather than percentiles. A percentile always
 * yields the same count however flat the world is, which would make a
 * featureless island claim the same number of wonders as a dramatic one.
 */

export type LandmarkKind =
  | 'peak' | 'waterfall' | 'confluence' | 'cliff' | 'pass' | 'lake' | 'grove';

/** §14's hierarchy. `common` is not in it: common ground is not a landmark. */
export type Rarity = 'notable' | 'rare' | 'exceptional';

export interface Landmark {
  kind: LandmarkKind;
  rarity: Rarity;
  /** Position IS the name (law 1). `landmark:<placeKey>`. */
  id: string;
  x: number; z: number;
  /** Metres above sea level of the cell it was found in. */
  height: number;
  /**
   * Kind-specific magnitude in its own units — metres of prominence, metres of
   * fall, discharge. Comparable WITHIN a kind and meaningless across kinds,
   * which is why rarity is cut per kind.
   */
  score: number;
  /** How wide the thing is, for the picker's footprint and the ring. */
  size: number;
  /** What it is called. Deterministic from position and seed. */
  name: string;
}

/**
 * The cuts, per kind, in each kind's own units. Chosen against the measured
 * distribution (`npx tsx tools/landmarks.ts`) rather than by feel: the target
 * shape is a handful of world-defining places, a few dozen rare ones and a
 * couple of hundred notable ones on an 883 km² island — roughly one thing
 * worth a detour every two or three kilometres of flight, which is a few
 * seconds apart at survey speed and minutes apart at looking speed.
 *
 * `min` is also the DETECTION floor: below it the feature is ordinary ground
 * and is never built at all, so the common tier costs nothing to skip.
 */
interface Cut { min: number; rare: number; exceptional: number }
const CUTS: Record<LandmarkKind, Cut> = {
  // metres of prominence above the surrounding country
  peak:       { min: 260,  rare: 900,    exceptional: 2100 },
  // discharge x metres of drop — the fall's own power
  waterfall:  { min: 3000, rare: 20000,  exceptional: 140000 },
  // sum of the two arms' discharge
  confluence: { min: 400,  rare: 1200,   exceptional: 3200 },
  // metres of fall inside one 520 m box. The island's median slope is 18.6°
  // (`tools/relief.ts`), so a steepness test names a third of the map; what
  // makes a cliff a landmark is how much of it there is in one drop. These are
  // the highest cuts of any kind for a reason: on a 2,560 m massif big drops
  // are the ISLAND'S normal, and the first pass made 33 of 56 exceptional
  // places cliffs, which is §14's failure mode exactly.
  cliff:      { min: 600,  rare: 1100,   exceptional: 2100 },
  // metres of shoulder standing over the saddle
  pass:       { min: 140,  rare: 220,    exceptional: 340 },
  // catchment held in a closed basin
  lake:       { min: 40,   rare: 400,    exceptional: 2800 },
  // per cent of the surrounding country that is NOT wood
  grove:      { min: 82,   rare: 90,     exceptional: 98.5 },
};

const rarityOf = (kind: LandmarkKind, score: number): Rarity =>
  score >= CUTS[kind].exceptional ? 'exceptional'
    : score >= CUTS[kind].rare ? 'rare' : 'notable';

/** One landmark per this many metres of any one kind — ten cliff cells are one cliff. */
const CLUSTER = 900;

/**
 * How wide the thing is on the ground, for the picker's tolerance and the ring.
 *
 * Derived from the feature's own magnitude rather than fixed per kind, because
 * a fixed number is wrong at both ends: a flat 300 m for `peak` meant that
 * looking at a 2,520 m mountain from three kilometres away named the ground
 * ("oak"), since the ray lands on the near flank a kilometre and a half short
 * of the summit — and the flank IS the mountain. A peak's footprint is the
 * country it stands over, which is what its prominence measures; a cliff's is
 * the length of the wall, which scales with its drop. The water features stay
 * small on purpose: a confluence is where two rivers actually meet, not the
 * valley containing it.
 */
function sizeOf(kind: LandmarkKind, score: number): number {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  switch (kind) {
    case 'peak':  return clamp(score * 0.6, 200, 1600);
    case 'cliff': return clamp(score * 0.35, 150, 700);
    case 'lake':  return clamp(Math.sqrt(score) * 9, 150, 600);
    case 'pass':  return 400;
    case 'grove': return 700;          // GROVE_R is ~1 km of country
    case 'waterfall': return 120;
    case 'confluence': return 160;
  }
}

const WOODY = new Set(['redwood', 'conifer', 'oak', 'riparian']);
const PROM_R = 12;    // cells (~3.1 km) — the country a summit stands over
const GROVE_R = 4;    // cells (~1 km) — the country a grove stands in

/**
 * Every remarkable place on this region, strongest first.
 *
 * One full scan of the region grid. Deterministic in every respect: the scan
 * order is fixed, the clustering keeps the strongest of a neighbourhood, and
 * ties break on position. Costs one pass at world load and nothing after.
 */
export function findLandmarks(region: Region): Landmark[] {
  const { n, cell, seaLevel, heightScale, elev, flow, down, ocean, biome } = region;
  const idx = (x: number, y: number) => y * n + x;
  const wx = (x: number) => (x - n / 2) * cell;
  const wz = (y: number) => (y - n / 2) * cell;
  const H = (i: number) => (elev[i] - seaLevel) * heightScale;
  const isWood = (i: number) => WOODY.has(region.biomeKeys[biome[i]]);

  const found: Omit<Landmark, 'rarity' | 'id' | 'name'>[] = [];
  const add = (kind: LandmarkKind, x: number, y: number, score: number) => {
    if (score < CUTS[kind].min) return;
    found.push({ kind, x: wx(x), z: wz(y), height: H(idx(x, y)), score,
                 size: sizeOf(kind, score) });
  };

  for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) {
    const i = idx(x, y);
    if (ocean[i]) continue;

    // ---- cliff: vertical extent inside one 3x3 of 260 m cells -------------
    let hi = -Infinity, lo = Infinity;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const h = H(idx(x + dx, y + dy));
      if (h > hi) hi = h;
      if (h < lo) lo = h;
    }
    add('cliff', x, y, hi - lo);

    // ---- waterfall: a real channel over a real step ----------------------
    // Reaching the sea is a river mouth, not a fall, so `down` into ocean is
    // skipped. Power is discharge x drop, which is literally what a fall is.
    const d = down[i];
    if (d >= 0 && !ocean[d]) add('waterfall', x, y, flow[i] * Math.max(0, H(i) - H(d)));

    // ---- confluence: two rivers, not a brook joining a river -------------
    const arms: number[] = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const j = idx(x + dx, y + dy);
      if (down[j] === i && !ocean[j]) arms.push(flow[j]);
    }
    if (arms.length >= 2) {
      arms.sort((a, b) => b - a);
      // the SMALLER arm has to be a river too, or every trickle is a junction
      if (arms[1] >= 60) add('confluence', x, y, arms[0] + arms[1]);
    }

    // ---- lake: a basin the hydrology had to fill -------------------------
    if (H(i) >= 20) {
      let sink = true;
      for (let dy = -1; dy <= 1 && sink; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        if (elev[idx(x + dx, y + dy)] < elev[i]) { sink = false; break; }
      }
      if (sink) add('lake', x, y, flow[i]);
    }
  }

  // ---- pass: a col, low across one axis and high across the other --------
  for (let y = 2; y < n - 2; y++) for (let x = 2; x < n - 2; x++) {
    const i = idx(x, y);
    if (ocean[i] || H(i) < 80) continue;
    const e = elev[i];
    const ewHigh = elev[idx(x - 2, y)] > e && elev[idx(x + 2, y)] > e;
    const nsLow = elev[idx(x, y - 2)] < e && elev[idx(x, y + 2)] < e;
    const nsHigh = elev[idx(x, y - 2)] > e && elev[idx(x, y + 2)] > e;
    const ewLow = elev[idx(x - 2, y)] < e && elev[idx(x + 2, y)] < e;
    if (!((ewHigh && nsLow) || (nsHigh && ewLow))) continue;
    const shoulder = ewHigh
      ? Math.min(H(idx(x - 2, y)), H(idx(x + 2, y))) - H(i)
      : Math.min(H(idx(x, y - 2)), H(idx(x, y + 2))) - H(i);
    add('pass', x, y, shoulder);
  }

  // ---- peak: prominence, not altitude ------------------------------------
  // On a 2,560 m massif the tenth-highest cell is not a mountain, it is the
  // same mountain. What earns a name is standing over your own country. True
  // prominence needs the key col to a higher summit; this takes height above
  // the lowest ground within 3.1 km, which ranks the same and costs one pass.
  for (let y = PROM_R; y < n - PROM_R; y++) for (let x = PROM_R; x < n - PROM_R; x++) {
    const i = idx(x, y);
    if (ocean[i]) continue;
    let isMax = true, low = Infinity;
    for (let dy = -PROM_R; dy <= PROM_R && isMax; dy++)
      for (let dx = -PROM_R; dx <= PROM_R; dx++) {
        if (dx * dx + dy * dy > PROM_R * PROM_R) continue;
        const j = idx(x + dx, y + dy);
        if (elev[j] > elev[i]) { isMax = false; break; }
        const h = H(j);
        if (h < low) low = h;
      }
    if (isMax) add('peak', x, y, H(i) - Math.max(0, low));
  }

  // ---- grove: old wood with nothing like it around it --------------------
  // §13's isolated old-growth grove. A redwood cell inside a redwood forest is
  // forest; a stand of redwood in chaparral is a landmark, and reads as one
  // from the air because the silhouettes differ.
  for (let y = GROVE_R; y < n - GROVE_R; y++) for (let x = GROVE_R; x < n - GROVE_R; x++) {
    const i = idx(x, y);
    if (ocean[i] || !isWood(i)) continue;
    let wood = 0, total = 0;
    for (let dy = -GROVE_R; dy <= GROVE_R; dy++) for (let dx = -GROVE_R; dx <= GROVE_R; dx++) {
      const j = idx(x + dx, y + dy);
      if (ocean[j]) continue;
      total++;
      if (isWood(j)) wood++;
    }
    if (total >= 20) add('grove', x, y, (1 - wood / total) * 100);
  }

  // ---- one place per neighbourhood, keeping the strongest -----------------
  // Ties break on position so the result cannot depend on scan order.
  const sorted = found.sort((a, b) =>
    b.score - a.score || a.x - b.x || a.z - b.z);
  const keep: typeof sorted = [];
  for (const m of sorted) {
    let near = false;
    for (const k of keep) {
      if (k.kind !== m.kind) continue;
      if (Math.hypot(k.x - m.x, k.z - m.z) < CLUSTER) { near = true; break; }
    }
    if (!near) keep.push(m);
  }

  // Names are drawn from small word lists, so on an island with hundreds of
  // places collisions are certain — the first run named three separate
  // cataracts "the Veil", which makes "there is nowhere else like it" a lie.
  // Resolved in the list's own fixed order, strongest first, so the result is
  // still a pure function of the seed: the biggest waterfall keeps the bare
  // name and the next one takes an epithet. Deterministic, and it reads right:
  // there is one Veil, and then there is the Grey Veil somewhere else.
  const taken = new Set<string>();
  return keep.map((m) => {
    const rarity = rarityOf(m.kind, m.score);
    const wanted = landmarkNames(m.kind, rarity, m.x, m.z, region.seed);
    const name = wanted.find((c) => !taken.has(c)) ?? wanted[0];
    taken.add(name);
    // The id carries the KIND as well as the place. `placeKey` quantises, so a
    // lake sitting in the basin a confluence feeds resolves to the same key —
    // and they are two different things standing in one spot, not one thing.
    return { ...m, rarity, id: `landmark:${m.kind}:${placeKey(m.x, m.z)}`, name };
  });
}

// ---------------------------------------------------------------------------
// Names.
//
// §8 wants places to carry mortal names and §12 says some places should matter
// because they are MEMORABLE. An unnamed bump is terrain; "the Drowned Stair"
// is somewhere you went. The name is a pure function of (position, seed), so
// it is the same on every machine for ever and costs nothing to store — the
// same trick that makes a tree's position its name.
//
// Deliberately NOT a mortal name: nobody in the world has told the god
// anything. These are the world's own words, in the register everything else
// in `attention.ts` uses.

const EPITHET = [
  'Grey', 'Long', 'Drowned', 'Silent', 'Old', 'Cold', 'Broken', 'High',
  'Hidden', 'Black', 'White', 'Crooked', 'Still', 'Bright', 'Deep', 'Bare',
  'Far', 'Green', 'Sunken', 'Narrow', 'Bitter', 'Quiet', 'Red', 'Lost',
  'Sharp', 'Wide', 'Sweet', 'Wild',
] as const;

const NOUN: Record<LandmarkKind, readonly string[]> = {
  peak:       ['Horn', 'Crown', 'Watch', 'Tooth', 'Head', 'Shoulder',
               'Spire', 'Cap', 'Seat', 'Finger'],
  waterfall:  ['Stair', 'Fall', 'Veil', 'Race', 'Spout', 'Leap',
               'Ladder', 'Throat', 'Curtain', 'Shout'],
  confluence: ['Meeting', 'Fork', 'Wedding', 'Mouth', 'Join',
               'Handshake', 'Knot', 'Braid', 'Council', 'Gathering'],
  cliff:      ['Face', 'Scarp', 'Wall', 'Brow', 'Drop', 'Reach',
               'Step', 'Shelf', 'Cheek', 'Edge'],
  pass:       ['Gate', 'Saddle', 'Notch', 'Way', 'Neck',
               'Door', 'Yoke', 'Gap', 'Stile', 'Crossing'],
  lake:       ['Eye', 'Bowl', 'Mere', 'Tarn', 'Well',
               'Cup', 'Mirror', 'Pool', 'Basin', 'Sink'],
  grove:      ['Stand', 'Wood', 'Company', 'Elders', 'Ring',
               'Congregation', 'Few', 'Holt', 'Copse', 'Assembly'],
};

/**
 * The names this place would accept, in order of preference.
 *
 * A *list* rather than one name, because the island has 403 places and the
 * word lists are small: three separate cataracts were named "the Veil" on the
 * first run, which makes "there is nowhere else like it" a lie. `findLandmarks`
 * walks this list and takes the first name nobody stronger has claimed, in its
 * own fixed strongest-first order — so the biggest waterfall keeps the bare
 * name and the next one along becomes the Grey Veil. Still a pure function of
 * (position, seed): the same island always resolves the same way.
 */
export function landmarkNames(
  kind: LandmarkKind, rarity: Rarity, x: number, z: number, seed: number,
): string[] {
  const nouns = NOUN[kind];
  const a = hash01(Math.round(x), Math.round(z), seed ^ 0x1a7);
  const b = hash01(Math.round(z), Math.round(x), seed ^ 0x2b9);
  const n0 = Math.floor(b * nouns.length) % nouns.length;
  const e0 = Math.floor(a * EPITHET.length) % EPITHET.length;
  const out: string[] = [];
  // the exceptional few prefer a bare definite name, so "the Horn" reads as one
  // of a handful and "the Grey Horn" as one of many
  if (rarity === 'exceptional') out.push(`the ${nouns[n0]}`);
  for (let k = 0; k < EPITHET.length * nouns.length; k++) {
    const e = (e0 + k) % EPITHET.length;
    const nn = (n0 + Math.floor((e0 + k) / EPITHET.length)) % nouns.length;
    out.push(`the ${EPITHET[e]} ${nouns[nn]}`);
  }
  return out;
}

/** The name this place prefers. Deterministic from position and seed (law 1). */
export const nameLandmark = (
  kind: LandmarkKind, rarity: Rarity, x: number, z: number, seed: number,
): string => landmarkNames(kind, rarity, x, z, seed)[0];

/**
 * What the chrome says about one, in the world's voice.
 *
 * The peak line reads its two numbers carefully. Prominence is measured
 * against the lowest ground within 3.1 km, so on the island's highest summits
 * that floor IS sea level and prominence equals altitude — which printed as
 * *"2520 m, and it stands 2520 over its country"*, a proxy's artefact showing
 * through as player-facing text. When the two agree the second clause is not
 * a second fact, so it is not said twice.
 */
export function describeLandmark(m: Landmark): { name: string; note?: string } {
  const h = Math.round(m.height), prom = Math.round(m.score);
  const peak = prom >= h - 40
    ? `${h} m, and it stands clear to the sea`
    : `${h} m, and it stands ${prom} over its own country`;
  const note: Record<LandmarkKind, string> = {
    peak,
    waterfall: 'the water falls here',
    confluence: 'two rivers meet',
    cliff: `${prom} m of rock, straight down`,
    pass: 'a way through the high ground',
    lake: 'water with nowhere to go',
    grove: 'old wood, alone in open country',
  };
  return {
    name: m.name,
    note: m.rarity === 'exceptional' ? `${note[m.kind]} — there is nowhere else like it`
      : note[m.kind],
  };
}

/**
 * The nearest landmark to a world point, within `within` metres — the picker's
 * entry point. Proximity against a list the world already keeps, in the same
 * shape as `Rocks.nearestTo` and `Mortals.nearestTo`, because §11 established
 * that raycasting island-wide meshes costs the whole frame budget.
 *
 * Reading this writes nothing (law 9). There is nowhere for it to write.
 */
export function nearestLandmark(
  list: readonly Landmark[], x: number, z: number,
  /** The picker's own tolerance for a thing of this footprint, in metres. */
  reach: (size: number) => number,
): Landmark | null {
  let best: Landmark | null = null, bestScore = 1;   // 1 = at the edge of reach
  for (const m of list) {
    const score = Math.hypot(m.x - x, m.z - z) / Math.max(1, reach(m.size));
    if (score < bestScore) { bestScore = score; best = m; }
  }
  return best;
}
